import { callAPI } from "../helper/apiUtils";

const CLASSIFY_CONCURRENCY = 6;

async function mapWithConcurrency(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runWorker = async () => {
    while (cursor < items.length) {
      const i = cursor;
      cursor += 1;
      try {
        results[i] = { status: "fulfilled", value: await worker(items[i], i) };
      } catch (reason) {
        results[i] = { status: "rejected", reason };
      }
    }
  };
  const workerCount = Math.min(Math.max(1, limit), items.length || 1);
  await Promise.all(Array.from({ length: workerCount }, runWorker));
  return results;
}

function apiData(res) {
  return res?.data || res;
}

function classifyFailed(data) {
  return !data || data.status === false;
}

function toIdList(values) {
  if (!Array.isArray(values)) return [];
  return values
    .map((x) => (x && typeof x === "object" ? x.id : x))
    .filter((id) => id != null && id !== "")
    .map(String);
}

/**
 * Mark as spam, then move remaining same-sender pending mail one-by-one so
 * the UI can show completed / total.
 */
export async function classifySpamWithProgress({ messageId, onProgress, extra = {} }) {
  onProgress?.({ completed: 0, total: 0, phase: "spam" });
  const res = await callAPI(`/email/classification/classify`, {}, "POST", {
    messageId,
    decision: "spam",
    cascade: false,
    ...extra,
  });
  const data = apiData(res);
  if (classifyFailed(data)) {
    const err = new Error((data && data.message) || "Failed to mark as spam");
    err.data = data;
    throw err;
  }
  const siblingIds = toIdList(data.sameSenderPending);
  const total = 1 + siblingIds.length;
  onProgress?.({ completed: 1, total, phase: "spam" });
  if (!siblingIds.length) return { autoLinkedIds: [], data };

  let completed = 1;
  await mapWithConcurrency(siblingIds, CLASSIFY_CONCURRENCY, async (id) => {
    await callAPI(`/email/classification/classify`, {}, "POST", {
      messageId: id,
      decision: "spam",
      cascade: false,
    });
    completed += 1;
    onProgress?.({ completed, total, phase: "spam" });
  });
  return { autoLinkedIds: siblingIds, data };
}

/**
 * File one email into a project, then find/link similar same-sender mail with
 * a determinate progress bar.
 */
export async function classifyProjectWithProgress({ messageId, body, onProgress }) {
  onProgress?.({ completed: 0, total: 0, phase: "saving" });
  const res = await callAPI(`/email/classification/classify`, {}, "POST", {
    ...body,
    messageId,
    decision: "project",
    skipAutoLink: true,
  });
  const data = apiData(res);
  if (classifyFailed(data)) {
    const err = new Error((data && data.message) || "Failed to save email to project");
    err.data = data;
    throw err;
  }
  const persistedId = (data.emailMessage && data.emailMessage.id) || messageId;
  const buildingId = (data.building && data.building.id) || body.buildingId;
  const conversationLinked = toIdList(data && data.autoLinked);
  if (!buildingId) return { autoLinkedIds: conversationLinked, data, persistedId };
  if (String(body.folder || "").toUpperCase() === "SENT") {
    return { autoLinkedIds: conversationLinked, data, persistedId };
  }
  onProgress?.({ completed: 0, total: 0, phase: "finding" });

  let matchIds = [];
  try {
    const sim = await callAPI(`/email/classification/similar-matches`, {}, "POST", {
      messageId: persistedId,
      buildingId,
    });
    const simData = apiData(sim);
    matchIds = toIdList(simData && simData.matches);
  } catch (e) {
    matchIds = [];
  }
  const already = new Set(conversationLinked);
  matchIds = matchIds.filter((id) => !already.has(String(id)));

  const total = 1 + conversationLinked.length + matchIds.length;
  onProgress?.({ completed: 1 + conversationLinked.length, total, phase: matchIds.length ? "linking" : "saving" });
  if (!matchIds.length) return { autoLinkedIds: conversationLinked, data, persistedId };

  let completed = 1 + conversationLinked.length;
  await mapWithConcurrency(matchIds, CLASSIFY_CONCURRENCY, async (id) => {
    await callAPI(`/email/classification/link`, {}, "POST", { messageId: id, buildingId });
    completed += 1;
    onProgress?.({ completed, total, phase: "linking" });
  });
  return { autoLinkedIds: [...conversationLinked, ...matchIds], data, persistedId };
}

export function classifyProgressLabel(progress, strings = {}) {
  const phase = progress && progress.phase;
  if (phase === "spam") return strings.email_associating_spam || "Moving to Spam...";
  if (phase === "finding") return strings.email_finding_similar || "Looking for similar emails...";
  if (phase === "linking") return strings.email_associating_similar || "Assigning similar emails...";
  return strings.email_associating_to_project || "Saving email to project...";
}
